/**
 * CombineEstimatesModal - Combine line items from multiple approved estimates into one job or invoice
 */
import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { 
  Briefcase, Receipt, Check, ArrowRight, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';

const CombineEstimatesModal = ({ open, onOpenChange, clientId, clientName, onComplete }) => {
  const { organization, user } = useAuth();
  const [mode, setMode] = useState('job'); // 'job' or 'invoice'
  const [estimates, setEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // selections: { [estimateId]: Set of line item indices }
  const [selections, setSelections] = useState({});
  const [expandedEstimates, setExpandedEstimates] = useState({});

  // Job/Invoice fields
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (open && clientId) {
      loadEstimates();
    }
  }, [open, clientId]);

  const loadEstimates = async () => {
    setIsLoading(true);
    try {
      const res = await apiHelpers.getApprovedEstimatesForClient(organization.id, clientId);
      setEstimates(res.data || []);
      // Auto-expand all
      const expanded = {};
      (res.data || []).forEach(e => { expanded[e.id] = true; });
      setExpandedEstimates(expanded);
    } catch (error) {
      toast.error('Failed to load estimates');
    } finally {
      setIsLoading(false);
    }
  };

  const getEstimateItems = (estimate) => {
    const dm = estimate.display_mode || 'detailed';
    if (dm === 'simple') {
      return (estimate.simple_pricing || []).map((p, i) => ({
        index: i, name: p.title || `Option ${i + 1}`, total: p.price || 0,
      }));
    }
    if (dm === 'summary') {
      const sections = estimate.summary_sections || [];
      const perSection = sections.length > 0 ? (estimate.summary_total || estimate.total || 0) / sections.length : 0;
      return sections.map((s, i) => ({
        index: i, name: s.title || `Section ${i + 1}`, total: perSection,
      }));
    }
    return (estimate.line_items || []).map((item, i) => ({
      index: i,
      name: item.name || item.description || `Item ${i + 1}`,
      qty: item.qty,
      unit: item.unit,
      rate: item.rate,
      total: item.total || (item.qty * item.rate),
    }));
  };

  const toggleItem = (estimateId, itemIndex) => {
    setSelections(prev => {
      const current = new Set(prev[estimateId] || []);
      if (current.has(itemIndex)) {
        current.delete(itemIndex);
      } else {
        current.add(itemIndex);
      }
      return { ...prev, [estimateId]: current };
    });
  };

  const toggleAllForEstimate = (estimateId, allItems) => {
    setSelections(prev => {
      const current = new Set(prev[estimateId] || []);
      const allSelected = allItems.every(item => current.has(item.index));
      if (allSelected) {
        return { ...prev, [estimateId]: new Set() };
      } else {
        return { ...prev, [estimateId]: new Set(allItems.map(i => i.index)) };
      }
    });
  };

  const totalSelected = useMemo(() => {
    let count = 0;
    let amount = 0;
    Object.entries(selections).forEach(([estId, indices]) => {
      const est = estimates.find(e => e.id === estId);
      if (!est) return;
      const items = getEstimateItems(est);
      indices.forEach(idx => {
        const item = items.find(i => i.index === idx);
        if (item) {
          count++;
          amount += item.total;
        }
      });
    });
    return { count, amount };
  }, [selections, estimates]);

  const handleSubmit = async () => {
    const sels = Object.entries(selections)
      .filter(([_, indices]) => indices.size > 0)
      .map(([estimateId, indices]) => ({
        estimate_id: estimateId,
        line_item_indices: Array.from(indices),
      }));

    if (sels.length === 0) {
      toast.error('Select at least one line item');
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === 'job') {
        const res = await apiHelpers.combineEstimatesToJob(
          organization.id,
          { selections: sels, title: title || undefined, start_date: startDate },
          user.id, user.name
        );
        toast.success(res.data.message);
      } else {
        const res = await apiHelpers.combineEstimatesToInvoice(
          organization.id,
          { selections: sels, due_date: dueDate || undefined },
          user.id, user.name
        );
        toast.success(res.data.message);
      }
      onComplete?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to combine estimates');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="combine-estimates-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Combine Estimates {clientName ? `for ${clientName}` : ''}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Select line items from multiple approved estimates to create a single {mode}.
          </p>
        </DialogHeader>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4" data-testid="combine-mode-toggle">
          <Button
            variant={mode === 'job' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('job')}
            data-testid="combine-mode-job"
          >
            <Briefcase className="w-4 h-4 mr-1" /> Combine to Job
          </Button>
          <Button
            variant={mode === 'invoice' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('invoice')}
            data-testid="combine-mode-invoice"
          >
            <Receipt className="w-4 h-4 mr-1" /> Combine to Invoice
          </Button>
        </div>

        {/* Job/Invoice config */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {mode === 'job' ? (
            <>
              <div>
                <Label className="text-xs">Job Title</Label>
                <Input
                  placeholder={`Combined Job - ${clientName || 'Client'}`}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="combine-job-title"
                />
              </div>
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="combine-start-date"
                />
              </div>
            </>
          ) : (
            <div>
              <Label className="text-xs">Due Date</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="h-8 text-sm"
                data-testid="combine-due-date"
              />
            </div>
          )}
        </div>

        {/* Estimates list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground" data-testid="combine-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading estimates...
          </div>
        ) : estimates.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="combine-no-estimates">
            No approved estimates found for this customer.
          </div>
        ) : (
          <div className="space-y-3" data-testid="combine-estimates-list">
            {estimates.map(est => {
              const estItems = getEstimateItems(est);
              const selectedForEst = selections[est.id] || new Set();
              const allSelected = estItems.length > 0 && estItems.every(i => selectedForEst.has(i.index));
              const expanded = expandedEstimates[est.id];

              return (
                <div key={est.id} className="border rounded-lg overflow-hidden" data-testid={`combine-estimate-${est.id}`}>
                  <div 
                    className="flex items-center justify-between px-3 py-2.5 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setExpandedEstimates(prev => ({ ...prev, [est.id]: !prev[est.id] }))}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleAllForEstimate(est.id, estItems); }}
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          allSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}
                        data-testid={`combine-select-all-${est.id}`}
                      >
                        {allSelected && <Check className="w-3 h-3 text-white" />}
                      </button>
                      <span className="text-sm font-medium truncate">
                        #{est.id?.slice(0, 8)} - {est.service_type || 'Estimate'}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {estItems.length} items
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        ${(est.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Badge>
                    </div>
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>

                  {expanded && (
                    <div className="p-2 space-y-1">
                      {estItems.map(item => {
                        const isSelected = selectedForEst.has(item.index);
                        return (
                          <button
                            key={item.index}
                            onClick={() => toggleItem(est.id, item.index)}
                            className={`w-full flex items-center gap-2 text-sm py-1.5 px-2 rounded transition-colors text-left ${
                              isSelected
                                ? 'bg-blue-50 border border-blue-300'
                                : 'bg-gray-50 hover:bg-blue-50/50 border border-transparent'
                            }`}
                            data-testid={`combine-item-${est.id}-${item.index}`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                            }`}>
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="flex-1 truncate font-medium">{item.name}</span>
                            {item.qty && <span className="text-xs text-muted-foreground">{item.qty} {item.unit}</span>}
                            <span className="text-muted-foreground">
                              ${item.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Summary bar */}
        {totalSelected.count > 0 && (
          <div className="flex items-center justify-between p-3 mt-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="combine-summary">
            <span className="text-sm font-medium text-blue-800">
              {totalSelected.count} items selected
            </span>
            <span className="text-sm font-semibold text-blue-900">
              ${totalSelected.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="combine-cancel-btn">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || totalSelected.count === 0}
            data-testid="combine-submit-btn"
          >
            {isSubmitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
            ) : (
              <><ArrowRight className="w-4 h-4 mr-2" /> Create {mode === 'job' ? 'Job' : 'Invoice'} ({totalSelected.count} items)</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CombineEstimatesModal;
